import type { Layout1538 } from './generated/layout';
import type { Palette as GeneratedPalette } from './generated/palette';
import type { PainterView as GeneratedPainterView } from './generated/painterView';
import type { LinearRGB } from '../core/color';
import type { Vec3 } from '../geo/types';

export type Layout = Layout1538;
export type RawPalette = GeneratedPalette;
export type RawPainterView = GeneratedPainterView;

export type Channel = Layout['water']['channels'][number];
export type Street = Layout['streets'][number];
export type Landmark = Layout['landmarks'][number];
export type WallRun = Layout['fortifications']['walls'][number];
export type Tower = Layout['fortifications']['towers'][number];
export type Gate = Layout['fortifications']['gates'][number];
export type Garden = Layout['gardens'][number];
export type Polder = Layout['polders'][number];

export type DataFile = 'layout' | 'palette' | 'painterView';

export interface DataError {
  readonly file: DataFile;
  readonly kind: 'fetch' | 'parse' | 'schema' | 'semantic';
  readonly message: string;
  readonly details: readonly string[];
}

export interface LoadProgress {
  readonly file: DataFile;
  readonly loadedBytes: number;
  readonly totalBytes: number | null;
}

export const REQUIRED_PALETTE_CLASSES = [
  'skyZenith',
  'skyMid',
  'skyHorizon',
  'cloudDark',
  'farFields',
  'nearFields',
  'polderFields',
  'ditchLines',
  'dikeRoads',
  'ijWater',
  'canalWater',
  'riverWater',
  'roofTile',
  'roofDark',
  'roofSlate',
  'wallLight',
  'wallBrick',
  'streetGround',
  'cityWall',
  'towerStone',
  'treeFoliage',
  'gardenGrass',
  'shipHull',
  'sail',
  'flagRed',
  'lineworkDark',
  'highlightWhite',
  'gunSmoke',
] as const;

export type PaletteClassName = (typeof REQUIRED_PALETTE_CLASSES)[number];

export interface Swatch {
  readonly hex: string;
  readonly linear: LinearRGB;
}

export interface WeightedSwatch extends Swatch {
  readonly weight: number;
}

export interface PaletteClass {
  readonly median: Swatch;
  readonly p10: Swatch;
  readonly p90: Swatch;
  readonly centres: readonly WeightedSwatch[];
}

export type PaletteModel = Readonly<Record<PaletteClassName, PaletteClass>>;

export interface CameraPose {
  readonly position: Vec3;
  readonly target: Vec3;
  readonly up: Vec3;
  readonly fovYDeg: number;
  readonly aspect: number;
}

export interface AppData {
  readonly layout: Layout;
  readonly palette: PaletteModel;
  readonly painterView: CameraPose;
}
